import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { MutedUser } from '@/api/chats'
import { isMUChat } from '@/utils/chats'
import type { LoadState } from '@/interfaces/general'
import type { Chat, ChatFile, ChatList, IGroupChatMember } from './IChats'

const initialState: {
  privateChats: ChatList,
  muChats: ChatList,
  privateChatsLoaded: LoadState,
  muChatsLoaded: LoadState,
  threadsLoaded: LoadState,
  activeChatId: string | null
} = {
  privateChats: {},
  muChats: {},
  privateChatsLoaded: 'unload',
  muChatsLoaded: 'unload',
  threadsLoaded: 'unload',
  activeChatId: null
}

interface UpdateMembersProps {
  chatJid: string,
  members: IGroupChatMember | IGroupChatMember[] | null
}

interface RemoveMembersProps {
  chatJid: string,
  memberJids: string[]
}

interface UpdateChatProps {
  chatJid: string,
  options: Partial<Chat>
}

interface AddFilesProps {
  chatJid: string,
  files: ChatFile[]
  loaded?: boolean
  before?: boolean
}

type RemoveFileProps = {
  chatJid: string
} & ({
  url: string
  uid?: string
} | {
  uid: string
  url?: string
})

type RemoveManyFilesProps = {
  chatJid: string
} & ({
  urls: string[]
  uids?: string[]
} | {
  uids: string[]
  urls?: string[]
})

const chats = createSlice({
  name: 'chats',
  initialState,
  reducers: {
    clear(state) {
      state.privateChats = {}
      state.muChats = {}
      state.privateChatsLoaded = 'unload'
      state.muChatsLoaded = 'unload'
      state.activeChatId = null
    },
    setPrivateChats(state, { payload }: { payload: ChatList }) {
      state.privateChats = payload
    },
    setPrivateChatsLoaded(state, { payload }: { payload: LoadState }) {
      state.privateChatsLoaded = payload
    },
    setThreadsLoaded(state, { payload }: { payload: LoadState }) {
      state.threadsLoaded = payload
    },
    setMUChats(state, { payload }: { payload: ChatList }) {
      state.muChats = payload
    },
    setMUChatsLoaded(state, { payload }: { payload: LoadState }) {
      state.muChatsLoaded = payload
    },
    setActiveChatId(state, { payload }: { payload: string | null }) {
      state.activeChatId = payload
    },
    _addMUChat(state, { payload }: { payload: Chat }) {
      if (state.muChats[payload.$jid]) {
        return
      }
      state.muChats[payload.$jid] = payload
    },
    _addPrivateChat(state, { payload }: PayloadAction<Chat>) {
      const chat = payload
      if (state.privateChats[chat.$jid]) {
        return
      }
      state.privateChats[chat.$jid] = chat
    },
    addChats(state, { payload }: PayloadAction<ChatList>) {
      Object.entries(payload).forEach(([key, chat]) => {
        if (isMUChat(chat)) {
          state.muChats[key] = chat
        } else {
          state.privateChats[key] = chat
        }
      })
    },
    deleteChats(state, { payload }: { payload: string | string[] }) {
      const deletedChatJids = Array.isArray(payload) ? payload : [payload]
      deletedChatJids.forEach(jid => {
        delete state.muChats[jid]
        delete state.privateChats[jid]
        if (state.activeChatId === jid) {
          state.activeChatId = null
        }
      })
    },
    updateChat(state, { payload }: { payload: UpdateChatProps }) {
      const { chatJid, options } = payload
      const chat = state.privateChats[chatJid] || state.muChats[chatJid]
      if (!chat) {
        return
      }
      const chats = chatJid.endsWith(`@conference.${import.meta.env.VITE_EJ_HOST}`) ?
        state.muChats :
        state.privateChats
      chats[chatJid] = {
        ...chat,
        ...options
      }
    },
    updateChats(state, { payload }: PayloadAction<UpdateChatProps[]>) {
      const allChats = { ...state.muChats, ...state.privateChats }
      payload.forEach(({chatJid, options}) => {
        const chat = allChats[chatJid]
        if (!chat) {
          return
        }
        const chats = chatJid.endsWith(`@conference.${import.meta.env.VITE_EJ_HOST}`) ?
          state.muChats :
          state.privateChats
        chats[chatJid] = {
          ...chat,
          ...options
        }
      })
    },
    addFiles(state, { payload }: { payload: AddFilesProps }) {
      const { chatJid, files, loaded, before } = payload
      const allChats = { ...state.muChats, ...state.privateChats }
      const chat = allChats[chatJid]
      if (!chat) {
        return
      }
      const list = chat.files?.list || []
      if (before) {
        list.unshift(...files)
      } else {
        list.push(...files)
      }
      const chats = isMUChat(chat) ? state.muChats : state.privateChats
      chats[chatJid] = {
        ...chat,
        files: {
          loaded: loaded != null ? loaded : chat.files?.loaded || false,
          list
        }
      }
    },
    removeFile(state, { payload }: { payload: RemoveFileProps }) {
      const { chatJid, url, uid } = payload
      const chat = state.muChats[chatJid] || state.privateChats[chatJid]
      if (!chat?.files || chat.files.list.length === 0) {
        return
      }
      const fileIndex = chat.files.list.findIndex(file =>
        (!!url && file.url === url) ||
        (!!uid && file.uid === uid))
      if (~fileIndex) {
        chat.files.list.splice(fileIndex, 1)
        return
      }
    },
    removeManyFiles(state, { payload }: { payload: RemoveManyFilesProps }) {
      const { chatJid, urls, uids } = payload
      if ((!urls || urls.length === 0) && (!uids || uids.length === 0)) {
        return
      }
      const chat = state.muChats[chatJid] || state.privateChats[chatJid]
      if (!chat?.files || chat.files.list.length === 0) {
        return
      }
      const urlSet = new Set(urls)
      const uidSet = new Set(uids)
      const filtered = chat.files.list.filter(file =>
        !urlSet.has(file.url) && !uidSet.has(file.uid))
      if (filtered.length !== chat.files.list.length) {
        chat.files.list = filtered
      }
    },
    updateMembers(state, { payload }: { payload: UpdateMembersProps }) {
      const { chatJid } = payload
      let { members } = payload
      if (!state.muChats[chatJid]) {
        return
      }
      if (members == null) {
        state.muChats[chatJid].members = members
        return
      }
      if (!Array.isArray(members)) {
        members = [members]
      }
      const membersMap = new Map<string, IGroupChatMember>();
      (state.muChats[chatJid].members || []).forEach(member => {
        membersMap.set(member.jid, member)
      })
      members.forEach(member => {
        const oldMember = membersMap.get(member.jid)
        membersMap.set(member.jid, {
          ...oldMember,
          ...member
        })
      })
      state.muChats[chatJid].members = Array.from(membersMap, ([_, value]) => value)
    },
    removeMembers(state, { payload }: { payload: RemoveMembersProps }) {
      const { chatJid, memberJids } = payload
      if (!state.muChats[chatJid]) {
        return
      }
      state.muChats[chatJid].members = state.muChats[chatJid].members
        ?.filter(member => !memberJids.includes(member.jid))
    },
    setMembersLoaded(state, { payload }: { payload: { chatJid: string, value: boolean } }) {
      const { chatJid, value } = payload
      if (!state.muChats[chatJid]) {
        return
      }
      state.muChats[chatJid].membersLoaded = value
    },
    setMutedUsers(state, { payload }: { payload: { chatJid: string, mutedUsers: MutedUser[] } }) {
      const { chatJid, mutedUsers } = payload
      if (!state.muChats[chatJid]) {
        return
      }
      const members = new Map()
      state.muChats[chatJid].members?.forEach(member => {
        members.set(member.jid, member)
      })
      mutedUsers.forEach(mutedUser => {
        const member = members.get(mutedUser.jid)
        if (member) {
          member.muted = mutedUser.mute
        }
      })
      state.muChats[chatJid].members = Array.from(members, ([_, value]) => value)
    }
  }
})

export const {
  _addMUChat,
  _addPrivateChat
} = chats.actions

// public actions
export const {
  clear,
  setPrivateChats,
  setPrivateChatsLoaded,
  setThreadsLoaded,
  setMUChats,
  setMUChatsLoaded,
  setActiveChatId,
  deleteChats,
  addChats,
  updateChat,
  updateChats,
  addFiles,
  removeFile,
  removeManyFiles,
  updateMembers,
  removeMembers,
  setMembersLoaded,
  setMutedUsers
} = chats.actions

export default chats.reducer

export interface ThunkChatsStore {
  state: { chats: typeof initialState }
}

export const addMUChat = createAsyncThunk<
  void,
  Chat,
  ThunkChatsStore
>(
  'chats/addMUChat',
  (chat, { dispatch, getState }) => {
    const state = getState().chats
    if (state.muChats[chat.$jid]) {
      dispatch(updateChat({
        chatJid: chat.$jid,
        options: chat
      }))
    } else {
      dispatch(_addMUChat(chat))
    }
  }
)

export const addPrivateChat = createAsyncThunk<
  void,
  Chat,
  ThunkChatsStore
>(
  'chats/addPrivateChat',
  (chat, { dispatch, getState }) => {
    const state = getState().chats
    if (state.privateChats[chat.$jid]) {
      dispatch(updateChat({
        chatJid: chat.$jid,
        options: chat
      }))
    } else {
      dispatch(_addPrivateChat(chat))
    }
  }
)
